const SECTION_SELECTOR = '#MainContent > [id^="shopify-section-"]';
const NUMBER_CLASS = "jumplinks-section-number";
const ANCHOR_CLASS = "jumplinks-anchor";

class Jumplinks extends HTMLElement {
  connectedCallback() {
    this.anchors = [];
    // Sections can be added, removed or reordered in the theme editor, which shifts every number.
    this.onSectionChange = () => {
      this.renderAnchors();
      this.renderSectionNumbers();
    };

    document.addEventListener("shopify:section:load", this.onSectionChange);
    document.addEventListener("shopify:section:unload", this.onSectionChange);
    document.addEventListener("shopify:section:reorder", this.onSectionChange);

    this.renderAnchors();
    this.renderSectionNumbers();
    this.scrollToHash();
  }

  disconnectedCallback() {
    document.removeEventListener("shopify:section:load", this.onSectionChange);
    document.removeEventListener(
      "shopify:section:unload",
      this.onSectionChange,
    );
    document.removeEventListener(
      "shopify:section:reorder",
      this.onSectionChange,
    );

    this.clearAnchors();
    this.clearSectionNumbers();
  }

  /**
   * Gives each link's target section an element carrying the link's hash as its id, so the links
   * work as plain in-page anchors.
   */
  renderAnchors() {
    this.clearAnchors();
    const sections = this._sections();

    this.querySelectorAll("a[data-section-number]").forEach((link) => {
      const section = sections[parseInt(link.dataset.sectionNumber, 10) - 1];
      if (!section) return;

      const anchor = document.createElement("span");
      anchor.id = decodeURIComponent(link.hash.slice(1));
      anchor.className = ANCHOR_CLASS;
      section.prepend(anchor);
      this.anchors.push(anchor);
    });
  }

  clearAnchors() {
    this.anchors.forEach((anchor) => anchor.remove());
    this.anchors = [];
  }

  // Browsers differ on whether they still scroll to a fragment target added after parsing, so this
  // jumps explicitly.
  scrollToHash() {
    const anchor = this._anchorFor(window.location.hash);
    if (!anchor) return;

    // With the page's smooth scroll-behavior, Chrome animates its own scroll to the injected anchor
    // towards a target taken mid-load, and it carries on past this jump. Held at auto until then.
    const { style } = document.documentElement;
    style.scrollBehavior = "auto";

    const jump = () => {
      anchor.scrollIntoView({ behavior: "instant", block: "start" });
      requestAnimationFrame(() => style.removeProperty("scroll-behavior"));
    };

    if (document.readyState === "complete") {
      jump();
    } else {
      window.addEventListener("load", jump, { once: true });
    }
  }

  renderSectionNumbers() {
    this.clearSectionNumbers();
    if (!this.hasAttribute("show-section-numbers")) return;

    this._sections().forEach((section, index) => {
      const number = document.createElement("span");
      number.className = `${NUMBER_CLASS} caption-1-caps`;
      number.textContent = index + 1;
      number.setAttribute("aria-hidden", "true");

      // The number is absolutely positioned, so the section needs to be a containing block.
      if (getComputedStyle(section).position === "static") {
        section.style.position = "relative";
        section.dataset.jumplinksPositioned = "true";
      }

      section.prepend(number);
    });
  }

  clearSectionNumbers() {
    document
      .querySelectorAll(`.${NUMBER_CLASS}`)
      .forEach((number) => number.remove());

    document
      .querySelectorAll("[data-jumplinks-positioned]")
      .forEach((section) => {
        section.style.removeProperty("position");
        delete section.dataset.jumplinksPositioned;
      });
  }

  _anchorFor(hash) {
    const id = decodeURIComponent(hash.slice(1));
    return this.anchors.find((anchor) => anchor.id === id);
  }

  _sections() {
    return [...document.querySelectorAll(SECTION_SELECTOR)];
  }
}

if (!customElements.get("jumplinks-component")) {
  customElements.define("jumplinks-component", Jumplinks);
}
